import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PermissionsItem from './PermissionsItem/PermissionsItem';
import { IDropDownListItem } from '../../Types/DropDownList/IDropDownListItem';
import { ClientUserDisplayInfo } from '../../Types/ClientUserDisplayInfo';
import { ImplicitDiagnosticQuestionnaireAccessUser } from '../../Types/ImplicitDiagnosticQuestionnaireAccessUser';
import { axiosInstance } from '../../Authentication/AxiosInterceptor';
import LoadingIcon from '../Utils/LoadingIcon/LoadingIcon';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Store';
import { updateNotesPermissions, selectRolePermissions, updateRestrictClientAdminQuestionnaireAccess, getQuestionnaireByAreaId } from '../../Redux/Store/Slices/questionnaire';
import { NotesPermission } from '../../Types/Products/IProductQuestionnaire';
import { getEffectiveNotesDefaultPermission } from '../Products/notesDefaultPermission';
import { toast } from 'react-toastify';
import ConfirmAction from '../Utils/ConfirmAction/ConfirmAction';
import Tooltip from '../Utils/Tooltip/Tooltip';
import styles from './PermissionsPopup.module.css';

const ALL_USERS_OPTIONS: IDropDownListItem[] = [
  { text: 'None', value: 'None', colour: undefined, textColour: undefined },
  { text: 'Read', value: 'Read', colour: undefined, textColour: undefined },
  { text: 'Edit', value: 'Edit', colour: undefined, textColour: undefined },
];

/** Invited users with diagnostic Reader role cannot be given questionnaire notes edit access (max Read). */
const READER_NOTES_OPTIONS: IDropDownListItem[] = ALL_USERS_OPTIONS.filter((o) => o.value !== 'Edit');

const hasDiagnosticReaderRole = (roles: string[] | undefined) =>
  (roles ?? []).some((r) => r.trim().toLowerCase() === 'reader');

// Map "Edit" to "Editor" for backend
const mapRoleToBackend = (role: string): "Editor" | "Read" | "None" => {
  if (role === 'Edit') return 'Editor';
  return role as "Editor" | "Read" | "None";
};

// Map "Editor" to "Edit" for frontend
const mapRoleFromBackend = (role: string): string => {
  if (role === 'Editor') return 'Edit';
  return role;
};

type PermissionsPopupProps = {
  areaId?: string;
};

const splitRoleLabels = (roleLabel: string) =>
  roleLabel.split(',').map((s) => s.trim()).filter(Boolean);

/** Lowercase sub -> owner/lead flags from implicit access API (Owner/Lead segments only). */
const buildOwnerLeadMapFromImplicit = (users: ImplicitDiagnosticQuestionnaireAccessUser[]) => {
  const map = new Map<string, { isOwner: boolean; isLead: boolean }>();
  for (const u of users) {
    if (!u.sub) continue;
    const key = u.sub.toLowerCase();
    let isOwner = false;
    let isLead = false;
    for (const seg of splitRoleLabels(u.roleLabel || '')) {
      const lower = seg.toLowerCase();
      if (lower === 'owner') isOwner = true;
      if (lower === 'lead') isLead = true;
    }
    if (isOwner || isLead) {
      map.set(key, { isOwner, isLead });
    }
  }
  return map;
};

const ownerLeadFlagsFromInvitedRoles = (roles: string[] | undefined) => {
  let isOwner = false;
  let isLead = false;
  for (const r of roles ?? []) {
    const t = r.trim().toLowerCase();
    if (t === 'owner') isOwner = true;
    if (t === 'lead') isLead = true;
  }
  return { isOwner, isLead };
};

/** Implicit API + invited-user roles from GetAreaUserDisplayInfos (source of truth for area Owner/Lead on the list). */
const buildCombinedOwnerLeadMap = (
  implicitUsers: ImplicitDiagnosticQuestionnaireAccessUser[],
  invited: ClientUserDisplayInfo[]
) => {
  const map = buildOwnerLeadMapFromImplicit(implicitUsers);
  for (const user of invited) {
    if (!user.sub) continue;
    const key = user.sub.toLowerCase();
    const { isOwner, isLead } = ownerLeadFlagsFromInvitedRoles(user.roles);
    if (!isOwner && !isLead) continue;
    const prev = map.get(key);
    map.set(key, {
      isOwner: prev?.isOwner || isOwner,
      isLead: prev?.isLead || isLead,
    });
  }
  return map;
};

const PermissionsPopup = ({ areaId }: PermissionsPopupProps) => {
  const dispatch = useAppDispatch();
  const questionnaire = useAppSelector((state) => state.questionnaire.questionnaire);
  const rolePermissions = useAppSelector(selectRolePermissions());
  const currentUserSub = useAppSelector((state) => state.auth.user?.sub);
  
  const [allUsersDefault, setAllUsersDefault] = useState<string>('None');
  const [invitedUsers, setInvitedUsers] = useState<ClientUserDisplayInfo[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [userPermissions, setUserPermissions] = useState<Record<string, string>>({});
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [defaultInitialized, setDefaultInitialized] = useState<boolean>(false);
  const [restrictAdminQuestionnaire, setRestrictAdminQuestionnaire] = useState<boolean>(false);
  const [implicitAccessUsers, setImplicitAccessUsers] = useState<ImplicitDiagnosticQuestionnaireAccessUser[]>([]);
  const [loadingImplicitAccess, setLoadingImplicitAccess] = useState<boolean>(false);
  
  // Confirmation dialog state
  const [showConfirmAllUsers, setShowConfirmAllUsers] = useState<boolean>(false);
  const [showConfirmIndividual, setShowConfirmIndividual] = useState<boolean>(false);
  const [pendingAllUsersChange, setPendingAllUsersChange] = useState<string | null>(null);
  const [pendingIndividualChange, setPendingIndividualChange] = useState<{ userSub: string; role: string } | null>(null);

  // Load existing notesPermissions and default from questionnaire (only on initial load)
  useEffect(() => {
    if (questionnaire && !defaultInitialized) {
      console.log('Loading permissions from questionnaire:', {
        notesDefaultPermission: questionnaire.notesDefaultPermission,
        notesPermissions: questionnaire.notesPermissions
      });
      
      // Load user permissions (individual overrides)
      if (questionnaire.notesPermissions) {
        const permissionsMap: Record<string, string> = {};
        questionnaire.notesPermissions.forEach((perm) => {
          permissionsMap[perm.userSub] = mapRoleFromBackend(perm.role);
        });
        console.log('Loaded user permissions map:', permissionsMap);
        setUserPermissions(permissionsMap);
      }
      
      const frontendDefault = mapRoleFromBackend(getEffectiveNotesDefaultPermission(questionnaire));
      console.log('Setting default (effective):', frontendDefault);
      setAllUsersDefault(frontendDefault);
      
      setDefaultInitialized(true);
    }
  }, [questionnaire, defaultInitialized]);

  useEffect(() => {
    if (!questionnaire) return;
    setRestrictAdminQuestionnaire(!!questionnaire.restrictClientAdminQuestionnaireAccess);
  }, [questionnaire?.restrictClientAdminQuestionnaireAccess, questionnaire?.id]);

  // Update userPermissions when questionnaire.notesPermissions changes (but don't change default)
  // Also update default if it changes in Redux
  useEffect(() => {
    if (questionnaire && defaultInitialized) {
      if (questionnaire.notesPermissions) {
        const permissionsMap: Record<string, string> = {};
        questionnaire.notesPermissions.forEach((perm) => {
          permissionsMap[perm.userSub] = mapRoleFromBackend(perm.role);
        });
        console.log('Updating user permissions from Redux:', permissionsMap);
        setUserPermissions(permissionsMap);
      }
      
      // Update default from Redux (effective: unset field => Editor in UI)
      const frontendDefault = mapRoleFromBackend(getEffectiveNotesDefaultPermission(questionnaire));
      setAllUsersDefault((prev) => {
        if (prev !== frontendDefault) {
          console.log('Updating default from Redux:', frontendDefault, '(was:', prev, ')');
          return frontendDefault;
        }
        return prev;
      });
    }
  }, [questionnaire, defaultInitialized]);

  useEffect(() => {
    const fetchAreaUsers = async () => {
      if (!areaId || areaId === 'undefined') return;
      try {
        setLoadingUsers(true);
        const response = await axiosInstance.get<ClientUserDisplayInfo[]>(
          `GetAreaUserDisplayInfos/${areaId}`
        );
        setInvitedUsers(response.data ?? []);
      } catch (error) {
        console.error('Error fetching area users:', error);
        setInvitedUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchAreaUsers();
  }, [areaId]);

  useEffect(() => {
    if (!areaId || areaId === 'undefined') {
      setImplicitAccessUsers([]);
      return;
    }
    const fetchImplicitAccess = async () => {
      try {
        setLoadingImplicitAccess(true);
        const response = await axiosInstance.get<ImplicitDiagnosticQuestionnaireAccessUser[]>(
          `GetImplicitDiagnosticQuestionnaireAccessUsers/${areaId}`
        );
        setImplicitAccessUsers(response.data ?? []);
      } catch (error) {
        console.error('Error fetching implicit questionnaire access users:', error);
        setImplicitAccessUsers([]);
      } finally {
        setLoadingImplicitAccess(false);
      }
    };
    fetchImplicitAccess();
  }, [areaId]);

  const ownerLeadBySubLower = useMemo(
    () => buildCombinedOwnerLeadMap(implicitAccessUsers, invitedUsers),
    [implicitAccessUsers, invitedUsers]
  );

  const isCurrentUserDiagnosticOwner = useMemo(() => {
    if (!currentUserSub) return false;
    const info = ownerLeadBySubLower.get(currentUserSub.toLowerCase());
    return !!info?.isOwner;
  }, [ownerLeadBySubLower, currentUserSub]);

  const isDiagnosticOwnerOrLead = useCallback(
    (userSub: string) => {
      if (!userSub) return false;
      const info = ownerLeadBySubLower.get(userSub.toLowerCase());
      return !!(info?.isOwner || info?.isLead);
    },
    [ownerLeadBySubLower]
  );

  const getOwnerLeadBadges = useCallback(
    (userSub: string): string[] => {
      const info = ownerLeadBySubLower.get(userSub.toLowerCase());
      if (!info) return [];
      const badges: string[] = [];
      if (info.isOwner) badges.push('Owner');
      if (info.isLead) badges.push('Lead');
      return badges;
    },
    [ownerLeadBySubLower]
  );

  /** Owners first, then leads (non-owners), then everyone else; stable sort by name within each group. */
  const invitedUsersOrdered = useMemo(() => {
    const sortKey = (u: ClientUserDisplayInfo) =>
      (u.fullName || u.email || u.sub || '').toLocaleLowerCase();
    const tier = (user: ClientUserDisplayInfo) => {
      const info = ownerLeadBySubLower.get(user.sub.toLowerCase());
      if (info?.isOwner) return 0;
      if (info?.isLead) return 1;
      return 2;
    };
    return [...invitedUsers].sort((a, b) => {
      const d = tier(a) - tier(b);
      if (d !== 0) return d;
      return sortKey(a).localeCompare(sortKey(b), undefined, { sensitivity: 'base' });
    });
  }, [invitedUsers, ownerLeadBySubLower]);

  // Update all users to the default permission
  const updateAllUsersPermissions = useCallback(async (newDefault: string) => {
    if (!areaId || !invitedUsers.length) return;
    
    setIsUpdating(true);
    try {
      const backendRole = mapRoleToBackend(newDefault);
      
      // When setting default, we should clear individual overrides (set everyone to default)
      // OR keep existing overrides and only update users without overrides
      // For simplicity, we'll set everyone to the default and clear individual overrides
      const notesPermissions: NotesPermission[] = invitedUsers.map((user) => {
        if (isDiagnosticOwnerOrLead(user.sub)) {
          return { userSub: user.sub, role: 'Editor' as const };
        }
        if (hasDiagnosticReaderRole(user.roles) && backendRole === 'Editor') {
          return { userSub: user.sub, role: 'Read' as const };
        }
        return { userSub: user.sub, role: backendRole };
      });

      const request = {
        notesPermissions,
        notesDefaultPermission: backendRole
      };

      console.log('Updating all users permissions:', { areaId, request, newDefault });
      try {
        const result = await dispatch(updateNotesPermissions({ areaId, request })).unwrap();
        console.log('Successfully updated all users permissions - full response:', JSON.stringify(result, null, 2));
        console.log('Response type:', typeof result);
        console.log('Response notesDefaultPermission:', result?.notesDefaultPermission);
        console.log('Response notesPermissions:', result?.notesPermissions);
      
      // Update local state - clear individual overrides since everyone is now at default
      const newUserPermissions: Record<string, string> = {};
      // Don't set userPermissions - let them fall back to default
      setUserPermissions(newUserPermissions);
      
        const frontendDefault = mapRoleFromBackend(
          getEffectiveNotesDefaultPermission({ notesDefaultPermission: result?.notesDefaultPermission })
        );
        setAllUsersDefault(frontendDefault);
        
        toast.success(`All users permissions updated to "${newDefault}"`);
      } catch (updateError) {
        console.error('Error in updateNotesPermissions dispatch:', updateError);
        throw updateError; // Re-throw to be caught by outer catch
      }
    } catch (error) {
      console.error('Error updating all users permissions:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      toast.error('Failed to update all users permissions. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  }, [areaId, invitedUsers, dispatch, isDiagnosticOwnerOrLead]);

  // Update individual user permission
  const updateIndividualUserPermission = useCallback(async (userSub: string, newRole: string) => {
    if (!areaId) return;
    if (isDiagnosticOwnerOrLead(userSub)) return;

    setIsUpdating(true);
    try {
      // Get current permissions or create new array
      const currentPermissions = questionnaire?.notesPermissions || [];
      
      // Remove existing permission for this user if it exists
      const filteredPermissions = currentPermissions.filter((p) => p.userSub !== userSub);
      
      // Add new permission (Readers cannot be given Editor)
      const user = invitedUsers.find((u) => u.sub === userSub);
      let backendRole = mapRoleToBackend(newRole);
      if (
        user &&
        hasDiagnosticReaderRole(user.roles) &&
        !isDiagnosticOwnerOrLead(userSub) &&
        backendRole === 'Editor'
      ) {
        backendRole = 'Read';
      }
      const updatedPermissions: NotesPermission[] = [
        ...filteredPermissions,
        { userSub, role: backendRole },
      ];
      const request = {
        notesPermissions: updatedPermissions
        // Don't update notesDefaultPermission when changing individual user
      };
      
      console.log('Updating individual user permission:', { 
        areaId, 
        userSub, 
        userName: user?.fullName,
        newRole,
        effectiveRole: backendRole,
        request 
      });
      
      const result = await dispatch(updateNotesPermissions({ areaId, request })).unwrap();
      console.log('Successfully updated user permission:', result);
      
      const frontendRole = mapRoleFromBackend(backendRole);
      setUserPermissions((prev) => ({ ...prev, [userSub]: frontendRole }));
      
      toast.success(`Permission updated to "${frontendRole}" for ${user?.fullName || 'user'}`);
    } catch (error) {
      console.error('Error updating user permission:', error);
      toast.error('Failed to update user permission. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  }, [areaId, questionnaire?.notesPermissions, invitedUsers, dispatch, isDiagnosticOwnerOrLead]);

  const getPermissionForUser = useCallback(
    (userSub: string) => {
      if (isDiagnosticOwnerOrLead(userSub)) return 'Edit';
      const raw = userPermissions[userSub] ?? allUsersDefault;
      const user = invitedUsers.find((u) => u.sub.toLowerCase() === userSub.toLowerCase());
      if (
        user &&
        hasDiagnosticReaderRole(user.roles) &&
        !isDiagnosticOwnerOrLead(userSub) &&
        raw === 'Edit'
      ) {
        return 'Read';
      }
      return raw;
    },
    [userPermissions, allUsersDefault, isDiagnosticOwnerOrLead, invitedUsers]
  );

  const handleAllUsersDefaultChange = (item: IDropDownListItem) => {
    const newDefault = item.value as string;
    // Don't update state yet - wait for confirmation
    // Store the pending change and show confirmation
    setPendingAllUsersChange(newDefault);
    setShowConfirmAllUsers(true);
  };

  const confirmAllUsersChange = async () => {
    if (pendingAllUsersChange) {
      setAllUsersDefault(pendingAllUsersChange);
      await updateAllUsersPermissions(pendingAllUsersChange);
      setPendingAllUsersChange(null);
      setShowConfirmAllUsers(false);
    }
  };

  const cancelAllUsersChange = () => {
    setShowConfirmAllUsers(false);
    setPendingAllUsersChange(null);
    // Dropdown will revert to original value since we didn't update allUsersDefault
  };

  const handleIndividualUserChange = (userSub: string, item: IDropDownListItem) => {
    if (isDiagnosticOwnerOrLead(userSub)) return;
    const newRole = item.value as string;
    const rowUser = invitedUsers.find((u) => u.sub === userSub);
    if (rowUser && hasDiagnosticReaderRole(rowUser.roles) && newRole === 'Edit') return;
    // Don't update state yet - wait for confirmation
    // Store the pending change and show confirmation
    setPendingIndividualChange({ userSub, role: newRole });
    setShowConfirmIndividual(true);
  };

  const confirmIndividualChange = async () => {
    if (pendingIndividualChange) {
      await updateIndividualUserPermission(pendingIndividualChange.userSub, pendingIndividualChange.role);
      setPendingIndividualChange(null);
      setShowConfirmIndividual(false);
    }
  };

  const cancelIndividualChange = () => {
    setShowConfirmIndividual(false);
    setPendingIndividualChange(null);
    // Dropdown will revert to original value since we didn't update userPermissions
  };

  const canEditPermissions = rolePermissions?.canEditNotesPermissions ?? false;
  const permissionTooltipText = `Your role as a ${rolePermissions?.roleTitle || 'user'} means that you cannot update permission settings.`;

  const lockerSub = questionnaire?.restrictClientAdminQuestionnaireLockedBySub;
  const mayUnlockRestrict =
    (!!lockerSub?.trim() &&
      !!currentUserSub &&
      lockerSub.trim().toLowerCase() === currentUserSub.toLowerCase()) ||
    (!lockerSub?.trim() && isCurrentUserDiagnosticOwner);

  const restrictCheckboxDisabled =
    isUpdating ||
    !canEditPermissions ||
    (!restrictAdminQuestionnaire && !isCurrentUserDiagnosticOwner) ||
    (restrictAdminQuestionnaire && !mayUnlockRestrict);

  const restrictCheckboxTooltip =
    !canEditPermissions
      ? permissionTooltipText
      : !restrictAdminQuestionnaire && !isCurrentUserDiagnosticOwner
        ? 'Only the diagnostic owner can enable this restriction.'
        : restrictAdminQuestionnaire && !mayUnlockRestrict
          ? 'Only the user who locked the questionnaire can remove this restriction.'
          : '';

  const getInviteRowTooltipText = useCallback(
    (userSub: string) => {
      if (
        restrictAdminQuestionnaire &&
        questionnaire?.restrictClientAdminQuestionnaireLockedBySub?.trim()
      ) {
        return 'While the questionnaire is locked, the questionnaire permission shown here does not grant access to the diagnostic questionnaire. Only the user who locked it can access it until the lock is removed.';
      }
      const info = ownerLeadBySubLower.get(userSub.toLowerCase());
      if (info?.isOwner || info?.isLead) {
        if (info.isOwner && info.isLead) {
          return 'As an owner and a lead on this diagnostic, their questionnaire access cannot be restricted here: they always have edit access.';
        }
        if (info.isOwner) {
          return 'As the diagnostic owner, their questionnaire access cannot be restricted here: they always have edit access.';
        }
        return 'As a lead in this diagnostic, their questionnaire access cannot be restricted here: they always have edit access.';
      }
      const rowUser = invitedUsers.find((u) => u.sub.toLowerCase() === userSub.toLowerCase());
      if (rowUser && hasDiagnosticReaderRole(rowUser.roles)) {
        return 'Readers in a diagnostic cannot be given edit permission for questionnaire notes here; read is the highest level available for them.';
      }
      return permissionTooltipText;
    },
    [
      restrictAdminQuestionnaire,
      questionnaire?.restrictClientAdminQuestionnaireLockedBySub,
      ownerLeadBySubLower,
      invitedUsers,
      permissionTooltipText,
    ]
  );

  const handleRestrictAdminQuestionnaireChange = async () => {
    if (!areaId || !canEditPermissions) return;
    if (!restrictAdminQuestionnaire && !isCurrentUserDiagnosticOwner) return;
    if (restrictAdminQuestionnaire && !mayUnlockRestrict) return;
    const next = !restrictAdminQuestionnaire;
    setRestrictAdminQuestionnaire(next);
    setIsUpdating(true);
    try {
      await dispatch(
        updateRestrictClientAdminQuestionnaireAccess({
          areaId,
          restrictClientAdminQuestionnaireAccess: next,
        })
      ).unwrap();
      await dispatch(getQuestionnaireByAreaId({ areaId, force: true }));
      toast.success(
        next
          ? 'Only you can access this diagnostic questionnaire until you turn this off.'
          : 'Others may access the questionnaire again according to role rules and invitations.'
      );
    } catch (e) {
      console.error(e);
      setRestrictAdminQuestionnaire(!next);
      toast.error('Could not update questionnaire access setting. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const allUsersDefaultItem = (
    <PermissionsItem
      title="All users"
      description="Set questionnaire access for all invited users"
      options={ALL_USERS_OPTIONS}
      value={allUsersDefault}
      onChange={handleAllUsersDefaultChange}
      readonly={isUpdating || !canEditPermissions}
    />
  );

  const isQuestionnaireSoloLocked =
    restrictAdminQuestionnaire &&
    !!questionnaire?.restrictClientAdminQuestionnaireLockedBySub?.trim();

  const lockerSubEffective = questionnaire?.restrictClientAdminQuestionnaireLockedBySub?.trim();

  const lockerResolvedDisplayName = useMemo(() => {
    if (!lockerSubEffective) return null;
    const key = lockerSubEffective.toLowerCase();
    const fromInvited = invitedUsers.find((u) => u.sub?.toLowerCase() === key);
    if (fromInvited) {
      const label = (fromInvited.fullName || fromInvited.email || fromInvited.sub).trim();
      return label || null;
    }
    const fromImplicit = implicitAccessUsers.find((u) => u.sub?.toLowerCase() === key);
    if (fromImplicit) {
      const label = (fromImplicit.fullName || fromImplicit.email || fromImplicit.sub).trim();
      return label || null;
    }
    return null;
  }, [lockerSubEffective, invitedUsers, implicitAccessUsers]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Privacy Settings</h2>
      <p className={styles.intro}>
        Control which invited users can view or edit your diagnostic questionnaire.
      </p>
      <div className={styles.implicitAccessPanel} role="region" aria-labelledby="privacy-access-preview-title">
        <span className={styles.sectionLabel}>Access preview</span>
        <p id="privacy-access-preview-title" className={styles.implicitAccessTitle}>
          Who can access this questionnaire without an invite?
        </p>
        <Tooltip
          text={restrictCheckboxTooltip || permissionTooltipText}
          align="top"
          disabled={!restrictCheckboxDisabled}
        >
          <label className={styles.implicitAccessCheckboxRow}>
            <input
              type="checkbox"
              checked={restrictAdminQuestionnaire}
              onChange={handleRestrictAdminQuestionnaireChange}
              disabled={restrictCheckboxDisabled}
            />
            <span>Lock diagnostic questionnaire so only you can access it</span>
          </label>
        </Tooltip>
        {!restrictAdminQuestionnaire && (
          <div role="status" aria-live="polite">
            <p className={styles.implicitAccessIntro}>
              While this restriction is off, super admins, advanced admins and leads can view and edit your diagnostic
              questionnaire. They do not need to appear in the invited users list.
            </p>
            {loadingImplicitAccess ? (
              <div className={styles.loadingWrapCompact}>
                <LoadingIcon />
              </div>
            ) : implicitAccessUsers.length > 0 ? (
              <ul className={styles.implicitAccessList}>
                {implicitAccessUsers.map((u) => {
                  const primary = u.fullName || u.email || u.sub;
                  const showEmailSubline = Boolean(u.fullName && u.email);
                  return (
                    <li key={u.sub} className={styles.implicitAccessRow}>
                      <div className={styles.implicitAccessUser}>
                        <span className={styles.implicitAccessName}>{primary}</span>
                        {showEmailSubline ? (
                          <span className={styles.implicitAccessEmail}>{u.email}</span>
                        ) : null}
                      </div>
                      <div className={styles.implicitAccessBadges}>
                        {splitRoleLabels(u.roleLabel).map((seg) => {
                          const key = seg.trim().toLowerCase();
                          const badgeClass =
                            key === 'owner' || key === 'lead'
                              ? `${styles.roleBadge} ${styles.roleBadgeAccent}`
                              : styles.roleBadge;
                          return (
                            <span key={`${u.sub}-${seg}`} className={badgeClass}>
                              {seg}
                            </span>
                          );
                        })}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className={styles.implicitAccessMuted}>No users matched this preview for your client.</p>
            )}
          </div>
        )}
        {restrictAdminQuestionnaire && (
          <p className={styles.implicitAccessRestrictedNote}>
            Only the user who enabled this lock can view or edit the diagnostic questionnaire. Invited users and admins
            cannot access it until the lock is removed. Removing the lock requires the user who locked it.
          </p>
        )}
      </div>
      {isQuestionnaireSoloLocked && (
          <div className={styles.invitedLockNotice} role="status" aria-live="polite">
            <span className={styles.invitedLockNoticeLabel}>Locked</span>
            <p className={styles.invitedLockNoticeText}>
              The diagnostic questionnaire is locked. The permissions below show how access would work once it is
              unlocked. They do not grant access while the lock is on. Only{' '}
              <strong>{lockerResolvedDisplayName ?? 'the user who locked it'}</strong> can view or edit the questionnaire
              until they remove the lock.
            </p>
          </div>
        )}
      <Tooltip text={permissionTooltipText} align="top" disabled={canEditPermissions}>
        {allUsersDefaultItem}
      </Tooltip>
      <section className={styles.invitedSection}>
        <h3 className={styles.invitedTitle}>
          Invited Users ({loadingUsers ? '...' : invitedUsers.length})
        </h3>
        
        {loadingUsers && (
          <div className={styles.loadingWrap}>
            <LoadingIcon />
          </div>
        )}
        {isUpdating && (
          <div className={styles.loadingWrap}>
            <LoadingIcon />
          </div>
        )}
        {!loadingUsers &&
          !isUpdating &&
          invitedUsersOrdered.map((user) => {
            const ownerLeadLocked = isDiagnosticOwnerOrLead(user.sub);
            const readerNotesCapped =
              hasDiagnosticReaderRole(user.roles) && !ownerLeadLocked;
            const notesOptions =
              ownerLeadLocked || !readerNotesCapped ? ALL_USERS_OPTIONS : READER_NOTES_OPTIONS;
            const individualUserItem = (
              <PermissionsItem
                key={user.sub}
                title={user.fullName}
                description={user.email}
                badges={getOwnerLeadBadges(user.sub)}
                options={notesOptions}
                value={getPermissionForUser(user.sub)}
                onChange={(item) => handleIndividualUserChange(user.sub, item)}
                readonly={!canEditPermissions || ownerLeadLocked}
              />
            );

            return (
              <Tooltip
                key={user.sub}
                text={getInviteRowTooltipText(user.sub)}
                align="top"
                disabled={
                  isQuestionnaireSoloLocked
                    ? false
                    : canEditPermissions && !ownerLeadLocked && !readerNotesCapped
                }
              >
                {individualUserItem}
              </Tooltip>
            );
          })}
      </section>
      
      {/* Confirmation dialog for "All users by default" */}
      <ConfirmAction
        isVisible={showConfirmAllUsers}
        onClose={cancelAllUsersChange}
        clickHandler={confirmAllUsersChange}
        title="Change Default Permission for All Users"
        message={`Are you sure you want to change the default permission for users to "${pendingAllUsersChange}"? This will update permissions for ${invitedUsers.length} invited ${invitedUsers.length === 1 ? 'user' : 'users'}.`}
        confirmText="Yes, Update All Users"
        cancelText="Cancel"
        severity="warning"
        loading={isUpdating}
      />
      
      {/* Confirmation dialog for individual user */}
      <ConfirmAction
        isVisible={showConfirmIndividual}
        onClose={cancelIndividualChange}
        clickHandler={confirmIndividualChange}
        title="Change User Permission"
        message={`Are you sure you want to change ${invitedUsers.find(u => u.sub === pendingIndividualChange?.userSub)?.fullName || 'this user'}'s permission to "${pendingIndividualChange?.role}"?`}
        confirmText="Yes, Update Permission"
        cancelText="Cancel"
        severity="warning"
        loading={isUpdating}
      />
    </div>
  );
};

export default PermissionsPopup;
